import type { PlayerProfile } from '../profile/schema';
import { fishStudyView, reconcileFishStudies } from './studies';
import { levelForXp, resolveSpecies, type SimState } from '../sim/index';
import { anchorPaidTicks, type VoyageView } from '../voyage/index';
import { MURRIN_DESTINATIONS } from '../voyage/destinations';

const QUARRY_STUDY_TICKS = 30 * 60;
const LOCAL_DESTINATIONS = ['murrin-nearshore', 'murrin-outer-shelf'] as const;
const FIRST_ATLAS_REQUIREMENTS = ['quarry-study', ...LOCAL_DESTINATIONS, 'chart-room'] as const;

export interface GoalView {
  readonly id: string;
  readonly label: string;
  readonly progress: number;
  readonly target: number;
  readonly units: 'paid minutes' | 'arrivals' | 'marks';
  readonly reward: string;
  readonly complete: boolean;
}

export interface DestinationResult {
  readonly id: string;
  readonly purpose: 'quarry' | 'survey';
  readonly label: string;
  readonly reward: string;
  readonly promise: string;
}

/** The nearshore promise names the quarry this save has now, never a future fish. */
export function destinationResult(destinationId: string, state: SimState): DestinationResult | null {
  const destination = MURRIN_DESTINATIONS.find(candidate => candidate.id === destinationId);
  if (destination === undefined) return null;
  if (destination.purpose === 'quarry') {
    const quarry = resolveSpecies(state, levelForXp(state.xp));
    return { id: destination.arrivalResultId, purpose: destination.purpose, label: `${quarry.name} ground`, reward: `${destination.promise} A quarry-ground mark for ${quarry.name}.`, promise: destination.promise };
  }
  return { id: destination.arrivalResultId, purpose: destination.purpose, label: 'Outer shelf survey', reward: destination.promise, promise: destination.promise };
}

function unique(values: readonly string[], value: string): readonly string[] {
  return values.includes(value) ? values : [...values, value];
}

function leadingQuarry(profile: PlayerProfile, state: SimState): { readonly id: string; readonly ticks: number } {
  if (state.targetSpecies) return { id: state.targetSpecies, ticks: profile.targetPaidTicks[state.targetSpecies] ?? 0 };
  const [id, ticks] = Object.entries(profile.targetPaidTicks).sort(([a, aTicks], [b, bTicks]) => bTicks - aTicks || a.localeCompare(b))[0] ?? [resolveSpecies(state, levelForXp(state.xp)).id, 0];
  return { id, ticks };
}
function quarryFor(id: string, state: SimState) { return resolveSpecies({ ...state, targetSpecies: id }, levelForXp(state.xp)); }

/** Reconciles only the known paid clock; catches and any visual state are irrelevant. */
export function reconcileProgress(profile: PlayerProfile, state: SimState, _view: VoyageView): PlayerProfile {
  const paid = anchorPaidTicks(state);
  let next = profile;
  if (paid !== null && paid > profile.accountedPaidTicks) {
    const delta = paid - profile.accountedPaidTicks;
    next = { ...next, accountedPaidTicks: paid };
    // Natural-course time is valuable simulation time, but it is not an explicit quarry study.
    if (state.targetSpecies) next = { ...next, targetPaidTicks: { ...next.targetPaidTicks, [state.targetSpecies]: (next.targetPaidTicks[state.targetSpecies] ?? 0) + delta } };
  }
  if (Object.values(next.targetPaidTicks).some(ticks => ticks >= QUARRY_STUDY_TICKS)) next = { ...next, milestones: unique(next.milestones, 'quarry-study') };
  for (const id of LOCAL_DESTINATIONS) {
    if (next.arrivedDestinations.includes(id)) next = { ...next, milestones: unique(next.milestones, id) };
  }
  // The two introduced Murrin choices are the finite chart-room curriculum; the
  // twenty-place chart remains exploration, never a gate on the first ending.
  if (LOCAL_DESTINATIONS.every(id => next.arrivedDestinations.includes(id))) {
    next = { ...next, milestones: unique(next.milestones, 'chart-room') };
  }
  if (FIRST_ATLAS_REQUIREMENTS.every(id => next.milestones.includes(id))) {
    next = { ...next, milestones: unique(next.milestones, 'first-atlas') };
  }
  return reconcileFishStudies(next);
}

export function firstAtlasState(profile: PlayerProfile): 'in-progress' | 'ready' | 'complete' | 'continuing' {
  if (!FIRST_ATLAS_REQUIREMENTS.every(id => profile.milestones.includes(id))) return 'in-progress';
  if (profile.endingSeen && profile.milestones.includes('first-atlas-continued')) return 'continuing';
  if (profile.endingSeen) return 'complete';
  return 'ready';
}

export function currentGoal(profile: PlayerProfile, state: SimState, _view: VoyageView): GoalView {
  if (profile.endingSeen && firstAtlasState(profile) !== 'in-progress') {
    const atlas = fishStudyView(profile, levelForXp(state.xp));
    const selectedStudy = atlas.studies.find(study => study.id === state.targetSpecies && study.unlocked && !study.complete) ?? atlas.next;
    const reward = atlas.completed === atlas.total ? 'All twenty-four field notes and the closing letter are yours. Keep fishing for records or follow the sea.'
      : selectedStudy !== null ? `${selectedStudy.label}: ${selectedStudy.minutes.toFixed(1)} / 30 counted minutes. Each study reveals a field note; letters arrive at 6, 12 and 24.`
      : 'Keep fishing to unlock the next species, then choose it for a 30-minute study.';
    return { id: 'field-atlas', label: 'The Field Atlas', progress: atlas.completed, target: atlas.total, units: 'marks', reward, complete: atlas.completed === atlas.total };
  }
  const selected = leadingQuarry(profile, state);
  const quarry = quarryFor(selected.id, state);
  if (!profile.milestones.includes('quarry-study')) {
    return { id: 'quarry-study', label: `${quarry.name} quarry study`, progress: Math.min(selected.ticks, QUARRY_STUDY_TICKS) / 60, target: QUARRY_STUDY_TICKS / 60, units: 'paid minutes', reward: 'A dependable fishing-ground mark.', complete: selected.ticks >= QUARRY_STUDY_TICKS };
  }
  const arrived = LOCAL_DESTINATIONS.filter(id => profile.arrivedDestinations.includes(id)).length;
  if (arrived < LOCAL_DESTINATIONS.length) {
    return { id: 'murrin-marks', label: 'Murrin ground marks', progress: arrived, target: LOCAL_DESTINATIONS.length, units: 'arrivals', reward: 'Nearshore quarry and outer-shelf survey marks.', complete: false };
  }
  const atlas = firstAtlasState(profile);
  return { id: 'first-atlas', label: atlas === 'continuing' ? 'Continue the tide' : 'The First Atlas', progress: atlas === 'in-progress' ? 0 : 1, target: 1, units: 'marks', reward: 'The season continues after the atlas.', complete: atlas !== 'in-progress' };
}
